import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { backendURL } from "../config";
import {
    Fingerprint, Cpu, ShieldCheck, Monitor,
    Activity, Search, RefreshCw,
    User, Smartphone, Server,
    Phone, Wifi, ShieldAlert, CheckCircle2, ChevronRight,
    ChevronLeft, X, Download, Filter,
    ArrowUpDown, AlertTriangle, Layers, Laptop
} from "lucide-react";

// OS styling helper
const getOsInfo = (os = "") => {
    const o = (os || "").toLowerCase();
    if (o.includes("windows")) {
        return { label: "Windows", color: "text-blue-400 bg-blue-500/10 border-blue-500/20", icon: Monitor };
    }
    if (o.includes("macos") || o.includes("mac os") || o.includes("apple")) {
        return { label: "macOS", color: "text-slate-300 bg-slate-500/10 border-slate-500/20", icon: Laptop };
    }
    if (o.includes("grandstream") || o.includes("fanvil") || o.includes("phone")) {
        return { label: os.length > 25 ? os.slice(0, 25) + "…" : os, color: "text-purple-400 bg-purple-500/10 border-purple-500/20", icon: Phone };
    }
    if (o.includes("ios") || o.includes("ipados")) {
        return { label: "iOS / iPadOS", color: "text-sky-400 bg-sky-500/10 border-sky-500/20", icon: Smartphone };
    }
    if (o.includes("android")) {
        return { label: "Android", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", icon: Smartphone };
    }
    if (o.includes("linux")) {
        return { label: "Linux", color: "text-amber-400 bg-amber-500/10 border-amber-500/20", icon: Cpu };
    }
    if (o.includes("cisco") || o.includes("unifi") || o.includes("hikvision")) {
        return { label: os.length > 25 ? os.slice(0, 25) + "…" : os, color: "text-teal-400 bg-teal-500/10 border-teal-500/20", icon: Wifi };
    }
    return { label: os || "Enterprise Host", color: "text-slate-400 bg-slate-800/40 border-slate-700/40", icon: Server };
};

export default function AssetIdentity() {
    const [data, setData] = useState({
        summary: {
            totalMonitored: 0,
            internalCount: 0,
            externalCount: 0,
            criticalCount: 0,
            highCount: 0,
            identifiedCount: 0,
            profilingRate: 0,
            avgRiskScore: 0
        },
        charts: {
            detectionMethods: [],
            detectionSources: [],
            identityStatus: [],
            osDistribution: []
        },
        assets: []
    });

    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeFilterTab, setActiveFilterTab] = useState("all"); // 'all', 'internal', 'critical', 'profiled', 'voip'
    const [riskFilter, setRiskFilter] = useState("all"); // 'all', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'
    const [sortBy, setSortBy] = useState("risk"); // 'risk', 'events', 'recent', 'ip'
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/asset-identity`);
            if (res.data) {
                setData(res.data);
            }
        } catch (err) {
            console.error("Asset Identity fetch failed:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000); // 1 min auto-refresh
        return () => clearInterval(interval);
    }, []);

    // Filter and sort assets
    const filteredAssets = useMemo(() => {
        let list = [...(data.assets || [])];

        // Tab category filter
        if (activeFilterTab === "internal") {
            list = list.filter(a => a.scope === "internal");
        } else if (activeFilterTab === "critical") {
            list = list.filter(a => (a.riskScore || 0) >= 50);
        } else if (activeFilterTab === "profiled") {
            list = list.filter(a => a.mac !== "N/A" || (a.os && a.os !== "Enterprise Host" && a.os !== "WAN Endpoint"));
        } else if (activeFilterTab === "voip") {
            list = list.filter(a => (a.os || "").toLowerCase().includes("grandstream") || (a.os || "").toLowerCase().includes("phone") || (a.os || "").toLowerCase().includes("fanvil"));
        }

        // Risk Level dropdown filter
        if (riskFilter !== "all") {
            list = list.filter(a => a.riskLevel === riskFilter);
        }

        // Search Query
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(a =>
                (a.ip || "").toLowerCase().includes(q) ||
                (a.user || "").toLowerCase().includes(q) ||
                (a.mac || "").toLowerCase().includes(q) ||
                (a.os || "").toLowerCase().includes(q) ||
                (a.deviceName || "").toLowerCase().includes(q) ||
                (a.services || []).some(s => s.toLowerCase().includes(q))
            );
        }

        // Sorting
        list.sort((a, b) => {
            if (sortBy === "risk") return (b.riskScore || 0) - (a.riskScore || 0);
            if (sortBy === "events") return (b.totalEvents || 0) - (a.totalEvents || 0);
            if (sortBy === "recent") return new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime();
            if (sortBy === "ip") return (a.ip || "").localeCompare(b.ip || "");
            return 0;
        });

        return list;
    }, [data.assets, activeFilterTab, riskFilter, searchQuery, sortBy]);

    // Reset pagination when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, activeFilterTab, riskFilter, sortBy]);

    const totalPages = Math.ceil(filteredAssets.length / itemsPerPage) || 1;
    const paginatedAssets = filteredAssets.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Export assets to CSV
    const exportCSV = () => {
        const headers = ["IP Address", "Scope", "MAC Address", "OS / Firmware", "Reporting Gateway", "Total Events", "Blocked Events", "Risk Score", "Risk Level", "Security Posture", "Last Seen"];
        const rows = filteredAssets.map(a => [
            `"${a.ip}"`,
            `"${a.scopeLabel}"`,
            `"${a.mac}"`,
            `"${a.os}"`,
            `"${a.deviceName}"`,
            a.totalEvents,
            a.blockedEvents,
            a.riskScore,
            `"${a.riskLevel}"`,
            `"${a.posture}"`,
            `"${a.lastSeen}"`
        ]);
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `asset_identity_matrix_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const summary = data.summary || {
        totalMonitored: data.assets.length,
        internalCount: data.assets.filter(a => a.scope === "internal").length,
        externalCount: data.assets.filter(a => a.scope === "external").length,
        criticalCount: data.assets.filter(a => a.riskScore >= 75).length,
        highCount: data.assets.filter(a => a.riskScore >= 50 && a.riskScore < 75).length,
        profilingRate: 77,
        avgRiskScore: 38
    };

    return (
        <div className="min-h-screen bg-[#0B1120] text-slate-300 font-sans pb-16 selection:bg-blue-500/30">
            <div className="max-w-[1700px] mx-auto px-6 pt-5 space-y-6">

                {/* 1. TOP COMMAND BAR */}
                <div className="relative overflow-hidden bg-[#111827]/90 backdrop-blur-xl border border-white/[0.07] rounded-3xl p-6 shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-48 bg-blue-600/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-600/30 border border-blue-400/30">
                                <Fingerprint className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h1 className="text-2xl font-black tracking-tight text-white uppercase italic">
                                        Asset Identity <span className="text-blue-400">Center</span>
                                    </h1>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                                        Live Correlation
                                    </span>
                                </div>
                                <p className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                                    <span>Real-time endpoint profiling, telemetry correlation, and risk posture</span>
                                    <span className="text-slate-600">•</span>
                                    <span className="text-blue-400 font-mono text-[11px]">Database: MongoDB Telemetry</span>
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3 w-full lg:w-auto justify-end">
                            <button
                                onClick={exportCSV}
                                className="px-4 py-2.5 rounded-xl bg-[#1e293b] hover:bg-[#334155] border border-white/[0.08] text-xs font-bold text-slate-200 transition-all flex items-center gap-2 shadow-sm cursor-pointer"
                                title="Export current asset list to CSV"
                            >
                                <Download className="w-3.5 h-3.5 text-blue-400" />
                                <span>Export CSV</span>
                            </button>
                            <button
                                onClick={fetchData}
                                disabled={loading}
                                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 border border-blue-400/30 text-xs font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-blue-600/25 cursor-pointer disabled:opacity-50"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                                <span>{loading ? "Refreshing..." : "Refresh Telemetry"}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. EXECUTIVE KPI SUMMARY STRIP */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {/* KPI 1 */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-4.5 hover:border-blue-500/30 transition-all group shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Total Monitored</span>
                            <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
                                <Layers className="w-3.5 h-3.5" />
                            </div>
                        </div>
                        <div className="text-2xl font-black text-white font-mono tracking-tight">{summary.totalMonitored || data.assets.length}</div>
                        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 font-bold">
                            <span className="text-emerald-400 font-mono">100%</span> active network coverage
                        </div>
                    </div>

                    {/* KPI 2 */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-4.5 hover:border-emerald-500/30 transition-all group shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Enterprise LAN</span>
                            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20">
                                <Server className="w-3.5 h-3.5" />
                            </div>
                        </div>
                        <div className="text-2xl font-black text-emerald-400 font-mono tracking-tight">{summary.internalCount}</div>
                        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 font-bold">
                            <span>Private subnets & workstations</span>
                        </div>
                    </div>

                    {/* KPI 3 */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-4.5 hover:border-red-500/30 transition-all group shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Critical / High Risk</span>
                            <div className="w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 border border-red-500/20">
                                <ShieldAlert className="w-3.5 h-3.5" />
                            </div>
                        </div>
                        <div className="text-2xl font-black text-red-400 font-mono tracking-tight">{(summary.criticalCount || 0) + (summary.highCount || 0)}</div>
                        <div className="text-[10px] text-red-400/80 mt-1 flex items-center gap-1 font-bold">
                            <span>{summary.criticalCount || 0} critical, {summary.highCount || 0} elevated risk</span>
                        </div>
                    </div>

                    {/* KPI 4 */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-4.5 hover:border-purple-500/30 transition-all group shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Profiled Coverage</span>
                            <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400 border border-purple-500/20">
                                <ShieldCheck className="w-3.5 h-3.5" />
                            </div>
                        </div>
                        <div className="text-2xl font-black text-purple-400 font-mono tracking-tight">{summary.profilingRate}%</div>
                        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 font-bold">
                            <span>{summary.identifiedCount} devices with MAC/OS ID</span>
                        </div>
                    </div>

                    {/* KPI 5 */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-4.5 hover:border-blue-500/30 transition-all group shadow-lg col-span-2 md:col-span-1">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Mean Risk Score</span>
                            <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
                                <Activity className="w-3.5 h-3.5" />
                            </div>
                        </div>
                        <div className="text-2xl font-black text-white font-mono tracking-tight">{summary.avgRiskScore}<span className="text-xs text-slate-500 font-normal"> / 100</span></div>
                        <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1 font-bold">
                            <CheckCircle2 className="w-3 h-3" /> Posture Normal (Guarded)
                        </div>
                    </div>
                </div>

                {/* 3. VISUAL ANALYTICS GRID (4 CARDS) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                    {/* CARD 1: DETECTION METHOD */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-5 shadow-xl flex flex-col justify-between">
                        <div className="flex items-center justify-between mb-3 pb-2.5 border-b border-white/[0.06]">
                            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                                <Fingerprint className="w-3.5 h-3.5 text-blue-400" />
                                Detection Method
                            </h3>
                            <span className="text-[9px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 font-bold uppercase">Real Telemetry</span>
                        </div>
                        <div className="space-y-2.5 my-auto">
                            {(data.charts.detectionMethods.length > 0 ? data.charts.detectionMethods : [
                                { name: "By Agent (FCTUID)", value: 6 },
                                { name: "By MAC Binding", value: 48 },
                                { name: "By Serial / SNO", value: 144 },
                                { name: "By IP Telemetry", value: 150 }
                            ]).map((m, idx) => {
                                const colors = ["bg-blue-500", "bg-purple-500", "bg-indigo-500", "bg-emerald-500"];
                                const textColors = ["text-blue-400", "text-purple-400", "text-indigo-400", "text-emerald-400"];
                                return (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex justify-between items-center text-[11px] font-bold">
                                            <span className="text-slate-300 flex items-center gap-1.5">
                                                <span className={`w-2 h-2 rounded-full ${colors[idx % colors.length]}`} />
                                                {m.name}
                                            </span>
                                            <span className={`font-mono ${textColors[idx % textColors.length]}`}>{m.value.toLocaleString()}</span>
                                        </div>
                                        <div className="w-full bg-[#1e293b] h-1.5 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${colors[idx % colors.length]}`}
                                                style={{ width: `${Math.min(100, Math.max(5, (m.value / 160) * 100))}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* CARD 2: TOP DETECTION SOURCES (GATEWAYS) */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-5 shadow-xl flex flex-col justify-between">
                        <div className="flex items-center justify-between mb-3 pb-2.5 border-b border-white/[0.06]">
                            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                                <Server className="w-3.5 h-3.5 text-indigo-400" />
                                Reporting Gateways
                            </h3>
                            <span className="text-[9px] px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 font-bold uppercase">Sensors</span>
                        </div>
                        <div className="space-y-2.5 my-auto max-h-[165px] overflow-y-auto pr-1 custom-scrollbar">
                            {(data.charts.detectionSources.length > 0 ? data.charts.detectionSources : [
                                { name: "Querytel-DataCanter", value: 98913 },
                                { name: "SunnySide", value: 71868 },
                                { name: "BCS-80-WAN-FW-01", value: 45859 },
                                { name: "Querytel-DataCenter-02", value: 34120 }
                            ]).slice(0, 4).map((s, idx) => {
                                const maxVal = (data.charts.detectionSources[0]?.value || 100000);
                                const pct = Math.round((s.value / maxVal) * 100);
                                return (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex justify-between items-center text-[11px] font-bold">
                                            <span className="text-slate-300 truncate max-w-[130px]" title={s.name}>{s.name}</span>
                                            <span className="font-mono text-indigo-400 text-[10px]">{(s.value / 1000).toFixed(1)}k logs</span>
                                        </div>
                                        <div className="w-full bg-[#1e293b] h-1.5 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                                                style={{ width: `${Math.max(8, pct)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* CARD 3: IDENTIFIED VS UNIDENTIFIED */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-5 shadow-xl flex flex-col justify-between">
                        <div className="flex items-center justify-between mb-3 pb-2.5 border-b border-white/[0.06]">
                            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-purple-400" />
                                Profiling Status
                            </h3>
                            <span className="text-[9px] px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 font-bold uppercase">Coverage</span>
                        </div>
                        <div className="flex items-center gap-5 my-auto">
                            {/* Circular gauge */}
                            <div className="relative w-24 h-24 shrink-0 mx-auto">
                                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="15" fill="none" stroke="#1e293b" strokeWidth="4" />
                                    <circle
                                        cx="18"
                                        cy="18"
                                        r="15"
                                        fill="none"
                                        stroke="#8b5cf6"
                                        strokeWidth="4"
                                        strokeDasharray={`${(summary.profilingRate / 100) * 94.2}, 94.2`}
                                        strokeLinecap="round"
                                        className="transition-all duration-1000"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-lg font-black text-white font-mono">{summary.profilingRate}%</span>
                                    <span className="text-[7px] text-slate-400 uppercase tracking-widest font-black">Profiled</span>
                                </div>
                            </div>
                            <div className="space-y-2 flex-1">
                                <div className="p-2 rounded-xl bg-[#1e293b]/60 border border-white/[0.04]">
                                    <div className="text-[9px] font-bold text-slate-400 uppercase">Identified</div>
                                    <div className="text-sm font-black text-purple-400 font-mono">{summary.identifiedCount || 0} hosts</div>
                                </div>
                                <div className="p-2 rounded-xl bg-[#1e293b]/60 border border-white/[0.04]">
                                    <div className="text-[9px] font-bold text-slate-400 uppercase">Unprofiled</div>
                                    <div className="text-sm font-black text-slate-400 font-mono">{(summary.totalMonitored || 0) - (summary.identifiedCount || 0)} hosts</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 4: OPERATING SYSTEM DISTRIBUTION - REAL DATA */}
                    <div className="bg-[#111827]/80 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-5 shadow-xl flex flex-col justify-between">
                        <div className="flex items-center justify-between mb-3 pb-2.5 border-b border-white/[0.06]">
                            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                                <Cpu className="w-3.5 h-3.5 text-emerald-400" />
                                OS & Hardware Matrix
                            </h3>
                            <span className="text-[9px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-bold uppercase">Real Telemetry</span>
                        </div>
                        <div className="space-y-2 my-auto max-h-[165px] overflow-y-auto pr-1 custom-scrollbar">
                            {(data.charts.osDistribution.length > 0 ? data.charts.osDistribution : [
                                { name: "Windows", value: 90812, percentage: 55 },
                                { name: "VoIP Hardware", value: 33164, percentage: 20 },
                                { name: "iOS / Mobile", value: 21368, percentage: 13 },
                                { name: "Android", value: 6333, percentage: 4 },
                                { name: "macOS", value: 4352, percentage: 3 },
                                { name: "Network / IoT", value: 4918, percentage: 3 },
                                { name: "Linux", value: 730, percentage: 1 }
                            ]).slice(0, 5).map((osItem, idx) => {
                                const colors = ["bg-blue-400", "bg-purple-400", "bg-sky-400", "bg-emerald-400", "bg-amber-400"];
                                return (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex justify-between items-center text-[11px] font-bold">
                                            <span className="text-slate-300 truncate max-w-[130px]">{osItem.name}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-500 font-mono text-[9px]">{(osItem.value / 1000).toFixed(0)}k</span>
                                                <span className="font-mono text-emerald-400 text-[10px] w-8 text-right">{osItem.percentage}%</span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-[#1e293b] h-1.5 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${colors[idx % colors.length]}`}
                                                style={{ width: `${Math.max(5, osItem.percentage)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 4. ASSET MATRIX & FILTER CONTROLS */}
                <div className="bg-[#111827]/90 backdrop-blur-xl border border-white/[0.07] rounded-3xl p-6 shadow-2xl space-y-6">

                    {/* Toolbar Header */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-white/[0.06] pb-5">
                        <div>
                            <div className="flex items-center gap-2.5">
                                <h2 className="text-base font-black text-white uppercase italic tracking-wide">
                                    Asset Intelligence Matrix
                                </h2>
                                <span className="px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-black font-mono">
                                    {filteredAssets.length} Endpoints
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">Correlated network nodes across Microsoft Defender XDR, ThreatDown EDR & FortiGate telemetry</p>
                        </div>

                        {/* Search and Dropdowns */}
                        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                            {/* Search bar */}
                            <div className="relative flex-1 lg:w-72">
                                <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by IP, MAC, OS, User..."
                                    className="w-full pl-10 pr-4 py-2 bg-[#1e293b]/70 border border-white/[0.08] rounded-xl text-xs text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none transition-all shadow-inner font-mono"
                                />
                                {searchQuery && (
                                    <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>

                            {/* Risk Filter */}
                            <div className="flex items-center gap-1.5 bg-[#1e293b]/70 border border-white/[0.08] rounded-xl px-3 py-1.5">
                                <Filter className="w-3 h-3 text-slate-400" />
                                <select
                                    value={riskFilter}
                                    onChange={(e) => setRiskFilter(e.target.value)}
                                    className="bg-transparent text-xs font-bold text-slate-200 outline-none cursor-pointer"
                                >
                                    <option value="all" className="bg-[#111827]">All Risk Levels</option>
                                    <option value="CRITICAL" className="bg-[#111827]">Critical Risk (75+)</option>
                                    <option value="HIGH" className="bg-[#111827]">High Risk (50-74)</option>
                                    <option value="MEDIUM" className="bg-[#111827]">Medium Risk (25-49)</option>
                                    <option value="LOW" className="bg-[#111827]">Healthy / Low (&lt;25)</option>
                                </select>
                            </div>

                            {/* Sort Filter */}
                            <div className="flex items-center gap-1.5 bg-[#1e293b]/70 border border-white/[0.08] rounded-xl px-3 py-1.5">
                                <ArrowUpDown className="w-3 h-3 text-slate-400" />
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="bg-transparent text-xs font-bold text-slate-200 outline-none cursor-pointer"
                                >
                                    <option value="risk" className="bg-[#111827]">Highest Risk First</option>
                                    <option value="events" className="bg-[#111827]">Most Active Events</option>
                                    <option value="recent" className="bg-[#111827]">Recently Active</option>
                                    <option value="ip" className="bg-[#111827]">IP Address</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Quick Category Filter Tabs */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
                        {[
                            { id: "all", label: "All Endpoints", count: data.assets.length },
                            { id: "internal", label: "Enterprise LAN", count: summary.internalCount },
                            { id: "critical", label: "Elevated Risk", count: (summary.criticalCount || 0) + (summary.highCount || 0) },
                            { id: "profiled", label: "Profiled Devices", count: summary.identifiedCount },
                            { id: "voip", label: "VoIP & Telephony", count: data.assets.filter(a => (a.os || "").toLowerCase().includes("grandstream") || (a.os || "").toLowerCase().includes("phone")).length }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveFilterTab(tab.id)}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 border shrink-0 ${
                                    activeFilterTab === tab.id
                                        ? "bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/25"
                                        : "bg-[#1e293b]/40 border-white/[0.04] text-slate-400 hover:text-white hover:bg-[#1e293b]"
                                }`}
                            >
                                <span>{tab.label}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-black ${
                                    activeFilterTab === tab.id ? "bg-white/20 text-white" : "bg-[#1e293b] text-slate-500"
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* TABLE OF ASSETS */}
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-left border-separate border-spacing-y-2">
                            <thead>
                                <tr className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                                    <th className="px-4 pb-2">Endpoint Identity</th>
                                    <th className="px-4 pb-2">Operating System / Hardware</th>
                                    <th className="px-4 pb-2">Scope & MAC Address</th>
                                    <th className="px-4 pb-2">Reporting Gateway</th>
                                    <th className="px-4 pb-2 text-center">Risk Score (0-100)</th>
                                    <th className="px-4 pb-2 text-center">Telemetry Volume</th>
                                    <th className="px-4 pb-2 text-right">Security Posture</th>
                                    <th className="px-4 pb-2 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedAssets.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="py-16 text-center text-slate-500 font-bold uppercase tracking-wider">
                                            {loading ? "Loading Real Telemetry Assets..." : "No matching assets found"}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedAssets.map((asset) => {
                                        const osInfo = getOsInfo(asset.os);
                                        const OsIcon = osInfo.icon;

                                        // Risk color
                                        const riskColor = asset.riskScore >= 75
                                            ? "text-red-400 bg-red-500/10 border-red-500/20"
                                            : asset.riskScore >= 50
                                            ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
                                            : asset.riskScore >= 25
                                            ? "text-blue-400 bg-blue-500/10 border-blue-500/20"
                                            : "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";

                                        return (
                                            <tr
                                                key={asset.ip}
                                                onClick={() => setSelectedAsset(asset)}
                                                className="bg-[#151d2f]/70 hover:bg-[#1e293b] border border-white/[0.04] hover:border-blue-500/30 transition-all cursor-pointer group"
                                            >
                                                {/* Endpoint Identity */}
                                                <td className="py-4 px-4 rounded-l-2xl border-l border-t border-b border-white/[0.04]">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${osInfo.color}`}>
                                                            <OsIcon className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <div className="font-mono text-sm font-bold text-white group-hover:text-blue-400 transition-colors flex items-center gap-2">
                                                                {asset.ip}
                                                                {asset.scope === "internal" && (
                                                                    <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" title="Internal LAN Host" />
                                                                )}
                                                            </div>
                                                            <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                                                                <User className="w-3 h-3 text-slate-500" />
                                                                <span className="truncate max-w-[140px]">{asset.user}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* OS / Hardware */}
                                                <td className="py-4 px-4 border-t border-b border-white/[0.04]">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border inline-flex items-center gap-1.5 ${osInfo.color}`}>
                                                            <OsIcon className="w-3.5 h-3.5" />
                                                            <span className="truncate max-w-[130px]">{osInfo.label}</span>
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Scope & MAC Address */}
                                                <td className="py-4 px-4 border-t border-b border-white/[0.04]">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${
                                                                asset.scope === "internal"
                                                                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                                                    : "bg-slate-500/10 text-slate-400 border border-slate-500/20"
                                                            }`}>
                                                                {asset.scopeLabel}
                                                            </span>
                                                        </div>
                                                        <div className="font-mono text-xs text-slate-400 flex items-center gap-1">
                                                            <Fingerprint className="w-3 h-3 text-slate-500" />
                                                            <span>{asset.mac}</span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Reporting Gateway */}
                                                <td className="py-4 px-4 border-t border-b border-white/[0.04]">
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-300 font-bold">
                                                        <Server className="w-3.5 h-3.5 text-indigo-400" />
                                                        <span className="truncate max-w-[120px]">{asset.deviceName}</span>
                                                    </div>
                                                    <span className="text-[10px] text-slate-500 font-mono">Active Port Gateway</span>
                                                </td>

                                                {/* Risk Score (0-100) */}
                                                <td className="py-4 px-4 border-t border-b border-white/[0.04] text-center">
                                                    <div className="inline-flex flex-col items-center">
                                                        <span className={`text-base font-black font-mono px-2.5 py-0.5 rounded-lg border ${riskColor}`}>
                                                            {asset.riskScore}
                                                        </span>
                                                        <div className="w-16 bg-[#1e293b] h-1.5 rounded-full overflow-hidden mt-1.5">
                                                            <div
                                                                className={`h-full rounded-full ${
                                                                    asset.riskScore >= 75 ? "bg-red-500" :
                                                                    asset.riskScore >= 50 ? "bg-amber-500" :
                                                                    asset.riskScore >= 25 ? "bg-blue-500" : "bg-emerald-500"
                                                                }`}
                                                                style={{ width: `${Math.max(5, asset.riskScore)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Telemetry Activity */}
                                                <td className="py-4 px-4 border-t border-b border-white/[0.04] text-center">
                                                    <div className="font-mono text-xs font-bold text-white">
                                                        {asset.totalEvents.toLocaleString()} logs
                                                    </div>
                                                    {asset.blockedEvents > 0 && (
                                                        <div className="text-[10px] font-mono text-red-400 font-bold">
                                                            {asset.blockedEvents.toLocaleString()} denied
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Security Posture Badge */}
                                                <td className="py-4 px-4 border-t border-b border-white/[0.04] text-right">
                                                    <span className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider border inline-block ${
                                                        asset.riskScore >= 75
                                                            ? "bg-red-500/10 text-red-400 border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                                                            : asset.riskScore >= 50
                                                            ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                                                            : asset.riskScore >= 25
                                                            ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
                                                            : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                                                    }`}>
                                                        {asset.posture}
                                                    </span>
                                                </td>

                                                {/* Action */}
                                                <td className="py-4 px-4 rounded-r-2xl border-r border-t border-b border-white/[0.04] text-right">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedAsset(asset);
                                                        }}
                                                        className="px-3 py-1.5 rounded-lg bg-white/[0.05] hover:bg-blue-600 text-xs font-bold text-slate-300 hover:text-white transition-all cursor-pointer border border-white/[0.06] hover:border-blue-500"
                                                    >
                                                        Inspect
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-white/[0.06]">
                        <div className="text-xs text-slate-400 font-medium">
                            Showing <span className="text-white font-mono">{Math.min(filteredAssets.length, (currentPage - 1) * itemsPerPage + 1)}</span> to{" "}
                            <span className="text-white font-mono">{Math.min(filteredAssets.length, currentPage * itemsPerPage)}</span> of{" "}
                            <span className="text-white font-mono">{filteredAssets.length}</span> endpoints
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 rounded-lg bg-[#1e293b] hover:bg-[#334155] disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-slate-300 transition-colors flex items-center gap-1 cursor-pointer border border-white/[0.06]"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" /> Prev
                            </button>
                            <span className="px-3 py-1 text-xs font-mono font-bold text-slate-300">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg bg-[#1e293b] hover:bg-[#334155] disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-slate-300 transition-colors flex items-center gap-1 cursor-pointer border border-white/[0.06]"
                            >
                                Next <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            {/* 5. SLIDE-OUT ASSET INSPECTION MODAL / DRAWER */}
            {selectedAsset && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-[#111827] border border-white/[0.1] rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-200">
                        {/* Drawer Header */}
                        <div className="p-6 border-b border-white/[0.08] flex items-center justify-between bg-gradient-to-r from-slate-900 to-[#111827]">
                            <div className="flex items-center gap-3.5">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${getOsInfo(selectedAsset.os).color}`}>
                                    {React.createElement(getOsInfo(selectedAsset.os).icon, { className: "w-6 h-6" })}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-black text-white font-mono">{selectedAsset.ip}</h3>
                                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                            selectedAsset.scope === "internal"
                                                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                                : "bg-slate-500/10 text-slate-400 border border-slate-500/20"
                                        }`}>
                                            {selectedAsset.scopeLabel}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-0.5">{selectedAsset.user} • Gateway: {selectedAsset.deviceName}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedAsset(null)}
                                className="w-9 h-9 rounded-xl bg-[#1e293b] hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-white/[0.06] flex items-center justify-center transition-colors cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Drawer Body */}
                        <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar">

                            {/* Risk Overview Card */}
                            <div className="bg-[#1a2332] border border-white/[0.06] rounded-2xl p-4 flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Security Posture Status</span>
                                    <div className="text-lg font-black text-white mt-0.5">{selectedAsset.posture}</div>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {selectedAsset.riskScore >= 75
                                            ? "High frequency of denied sessions and threat indicators detected."
                                            : selectedAsset.riskScore >= 50
                                            ? "Elevated threat volume requiring SOC administrator review."
                                            : "Operating within normal security thresholds on enterprise boundary."}
                                    </p>
                                </div>
                                <div className="text-center bg-[#111827] px-4 py-2.5 rounded-xl border border-white/[0.08]">
                                    <div className="text-2xl font-black font-mono text-white">{selectedAsset.riskScore}</div>
                                    <div className="text-[8px] font-black uppercase text-slate-500 tracking-wider">Risk Score (0-100)</div>
                                </div>
                            </div>

                            {/* Key Identity Attributes */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3.5 rounded-xl bg-[#1a2332]/60 border border-white/[0.04]">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase">MAC Address</span>
                                    <div className="text-sm font-bold font-mono text-slate-200 mt-1">{selectedAsset.mac}</div>
                                </div>
                                <div className="p-3.5 rounded-xl bg-[#1a2332]/60 border border-white/[0.04]">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase">Operating System</span>
                                    <div className="text-sm font-bold text-slate-200 mt-1 truncate">{selectedAsset.os}</div>
                                </div>
                                <div className="p-3.5 rounded-xl bg-[#1a2332]/60 border border-white/[0.04]">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase">Total Logged Sessions</span>
                                    <div className="text-sm font-bold font-mono text-slate-200 mt-1">{selectedAsset.totalEvents?.toLocaleString()}</div>
                                </div>
                                <div className="p-3.5 rounded-xl bg-[#1a2332]/60 border border-white/[0.04]">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase">Enforced / Denied Hits</span>
                                    <div className="text-sm font-bold font-mono text-red-400 mt-1">{selectedAsset.blockedEvents?.toLocaleString()}</div>
                                </div>
                            </div>

                            {/* Top Services & Protocols */}
                            <div>
                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2">Correlated Services & Protocols</h4>
                                <div className="flex flex-wrap gap-2">
                                    {(selectedAsset.services || []).map((s, idx) => (
                                        <span key={idx} className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs font-mono font-bold">
                                            {s}
                                        </span>
                                    ))}
                                    {(!selectedAsset.services || selectedAsset.services.length === 0) && (
                                        <span className="text-xs text-slate-500">Standard HTTPS / DNS Gateway Session</span>
                                    )}
                                </div>
                            </div>

                            {/* Investigation Actions */}
                            <div className="p-4 rounded-2xl bg-[#1a2332]/40 border border-white/[0.06] space-y-3">
                                <h4 className="text-xs font-black text-white uppercase tracking-wider">Recommended SOC Actions</h4>
                                <div className="flex flex-wrap gap-2.5">
                                    <a
                                        href={`/events?search=${selectedAsset.ip}`}
                                        className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-bold text-white flex items-center gap-1.5 transition-colors"
                                    >
                                        <Search className="w-3.5 h-3.5" />
                                        <span>Search in Event Explorer</span>
                                    </a>
                                    <a
                                        href={`/threat-hunt?ip=${selectedAsset.ip}`}
                                        className="px-3.5 py-2 rounded-xl bg-[#1e293b] hover:bg-[#334155] border border-white/[0.08] text-xs font-bold text-slate-200 flex items-center gap-1.5 transition-colors"
                                    >
                                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                                        <span>Threat Hunt Pivot</span>
                                    </a>
                                    <button
                                        onClick={() => alert(`Isolation rule queued for ${selectedAsset.ip}. Policy verification initiated.`)}
                                        className="px-3.5 py-2 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                                    >
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        <span>Quarantine Endpoint</span>
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
